import {
  BAR_POINT,
  InProgressGameState,
  Model,
  OFF_POINT,
  opponent,
  opponentPoint,
  Player,
  User,
} from '@kfiroo/backgammon-core'

export enum PlayerType {
  PLAYER = 'player',
  OPPONENT = 'opponent',
}

export interface LaneInfo {
  checkers: number
  owner: PlayerType
}

export interface BoardInfo {
  lanes: Record<string, LaneInfo>
  bar: Record<PlayerType, number>
  off: Record<PlayerType, number>
}

export type PlayersInfo = Record<PlayerType, User | null>

export interface AnimatedMove {
  from: number
  to: number
  // Always OPPONENT for projected live moves; consumed by applyAnimatedMove to
  // pick which side's lane/bar/off to update.
  owner: PlayerType
  isHit: boolean
  isBearOff: boolean
}

export const EMPTY_BOARD: BoardInfo = {
  lanes: {},
  bar: {
    [PlayerType.PLAYER]: 0,
    [PlayerType.OPPONENT]: 0,
  },
  off: {
    [PlayerType.PLAYER]: 0,
    [PlayerType.OPPONENT]: 0,
  },
}

export function projectBoard(
  state: InProgressGameState | undefined,
  playerIndex: number,
): BoardInfo {
  if (state?.state === 'in-progress') {
    const resolvedIndex = (playerIndex === -1 ? 0 : playerIndex) as Player
    const opponentIndex = opponent(resolvedIndex)

    const playerBoard = state.board[resolvedIndex]
    const opponentBoard = state.board[opponentIndex]

    const lanes: BoardInfo['lanes'] = {}
    Object.entries(playerBoard).forEach(([point, count]) => {
      if (count) {
        lanes[point] = {
          owner: PlayerType.PLAYER,
          checkers: count,
        }
      }
    })
    Object.entries(opponentBoard).forEach(([point, count]) => {
      if (count) {
        lanes[opponentPoint(point)] = {
          owner: PlayerType.OPPONENT,
          checkers: count,
        }
      }
    })
    const bar: BoardInfo['bar'] = {
      [PlayerType.PLAYER]: playerBoard[BAR_POINT] ?? 0,
      [PlayerType.OPPONENT]: opponentBoard[BAR_POINT] ?? 0,
    }

    const off: BoardInfo['off'] = {
      [PlayerType.PLAYER]: playerBoard[OFF_POINT] ?? 0,
      [PlayerType.OPPONENT]: opponentBoard[OFF_POINT] ?? 0,
    }

    return {
      lanes,
      bar,
      off,
    }
  }
  return {
    lanes: {},
    bar: {
      [PlayerType.PLAYER]: 0,
      [PlayerType.OPPONENT]: 0,
    },
    off: {
      [PlayerType.PLAYER]: 0,
      [PlayerType.OPPONENT]: 0,
    },
  }
}

export function projectPlayers(model: Model, playerIndex: number): PlayersInfo {
  if (playerIndex === -1) {
    return {
      [PlayerType.PLAYER]: model.players[0],
      [PlayerType.OPPONENT]: model.players[1],
    }
  }
  return {
    [PlayerType.PLAYER]: model.players[playerIndex],
    [PlayerType.OPPONENT]: model.players[1 - playerIndex],
  }
}

const mapAnimatedPoint = (point: number): number =>
  point === BAR_POINT || point === OFF_POINT ? point : opponentPoint(point)

/**
 * The opponent's current-turn moves mapped into the watcher's POV. Returns []
 * when the game is not in progress, when it is the watcher's own turn (their
 * moves are instant), or when there is no live turn / no moves yet. A spectator
 * (playerIndex === -1) is treated as player 0, mirroring projectBoard — so they
 * see player 1's moves animated. Regular points flip via opponentPoint; BAR/OFF
 * stay as sentinels.
 */
export function projectLiveMoves(
  model: Model,
  playerIndex: number,
): AnimatedMove[] {
  const gs = model.gameState
  if (gs.state !== 'in-progress') {
    return []
  }
  const resolvedIndex = playerIndex === -1 ? 0 : playerIndex
  if (gs.turn === resolvedIndex) {
    return []
  }
  const live = gs.liveTurn
  if (live == null || live.moves.length === 0) {
    return []
  }
  // m.from / m.to are number (MoveInfo), not string board-entry keys
  return live.moves.map((m) => ({
    from: mapAnimatedPoint(m.from),
    to: mapAnimatedPoint(m.to),
    owner: PlayerType.OPPONENT,
    isHit: m.isHit,
    isBearOff: m.isBearOff,
  }))
}
